import { Component, type ReactNode, type ErrorInfo } from "react";
import { isStaleChunkError, reloadForStaleChunk } from "../lib/chunkReload";

/**
 * Sprint CT-3 : ErrorBoundary global pour le back-office.
 *
 * React, par defaut, demonte tout le sous-arbre quand un composant throw
 * pendant le render. Resultat : si une page (ex StatsPage) plante, on perd
 * aussi la sidebar Layout. Cet ErrorBoundary attrape l'erreur, affiche un
 * fallback dans la zone du child, et laisse intacte le reste de l'app.
 *
 * Place autour du <Outlet/> dans Layout pour preserver la sidebar.
 */
interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  /** Sprint CT-4 : inline = rendu plus compact pour wrapper un sous-element
   * (un chart, un panneau) sans casser tout le layout. */
  inline?: boolean;
  /** Label affiche dans le fallback inline (ex: "Graphique 'Distribution'"). */
  label?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.label ?? "page", error, info);
    // Sprint DN : chunk introuvable après un déploiement → on recharge pour
    // récupérer la nouvelle version (garde anti-boucle dans reloadForStaleChunk).
    if (isStaleChunkError(error)) {
      reloadForStaleChunk();
    }
  }

  reset = () => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) {
      // Sprint DN : erreur de chunk obsolète (post-déploiement). Plutôt que le
      // détail technique anxiogène, on affiche un message de mise à jour. Le
      // rechargement est déjà déclenché par componentDidCatch ; le bouton sert
      // de repli si l'auto-reload est en cooldown.
      if (isStaleChunkError(this.state.error)) {
        return (
          <div className="p-8 max-w-2xl">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
              <h1 className="text-lg font-semibold text-amber-900 mb-2">
                Une nouvelle version est disponible
              </h1>
              <p className="text-sm text-amber-800 mb-4">
                L'application a été mise à jour. Rechargement en cours pour
                récupérer la dernière version…
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 rounded-md text-sm bg-amber-600 text-white hover:bg-amber-700"
              >
                Recharger maintenant
              </button>
            </div>
          </div>
        );
      }
      // Mode inline : carre compact pour wrapper un chart / panneau
      if (this.props.inline) {
        return (
          <div className="p-6 rounded-lg border border-red-200 bg-red-50 text-sm">
            <p className="font-medium text-red-900 mb-1">
              {this.props.label ? `${this.props.label} — indisponible` : "Élément indisponible"}
            </p>
            <details className="text-xs text-red-800 font-mono whitespace-pre-wrap break-all mt-2">
              <summary className="cursor-pointer">Détails</summary>
              {this.state.error.message}
              {"\n"}
              {this.state.error.stack ?? ""}
            </details>
          </div>
        );
      }
      // Mode page complete (defaut)
      return (
        <div className="p-8 max-w-2xl">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <h1 className="text-lg font-semibold text-red-900 mb-2">
              Cette page a rencontré une erreur
            </h1>
            <p className="text-sm text-red-800 mb-4">
              Le reste de l'application reste utilisable. Vous pouvez naviguer
              vers une autre section via le menu de gauche, ou recharger la
              page.
            </p>
            <details className="text-xs text-red-900 mb-4 font-mono whitespace-pre-wrap break-all">
              <summary className="cursor-pointer mb-2">
                Détails techniques (pour le support)
              </summary>
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack ?? ""}
            </details>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={this.reset}
                className="px-3 py-1.5 rounded-md text-sm bg-white border border-red-200 hover:bg-red-100"
              >
                Réessayer
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 rounded-md text-sm bg-red-600 text-white hover:bg-red-700"
              >
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
